import Decimal from 'decimal.js';

import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { SubReserveAmount } from '~/server_shared/swagger/data-contracts';
import { convertAmountForLimitCheck } from '~/Utils/currencyUtils';
import toCents from '~/Utils/toCents';

// The VAT/GST netting a reserve movement is priced by when "Reduce reserves by Net paid" is on --
// the frontend mirror of the backend's `_get_vat_to_net_out` / `_apply_net_to_reserve_amount` /
// `_get_netted_sub_reserve_amounts`, so every above-reserve preview agrees with the gate that
// actually refuses the payment.

interface GetVatAmountToNetOutParams {
  shouldReduceReservesByNetPaid: boolean;
  isVatIncluded?: boolean | null;
  vatAmount?: number | string | null;
  requestCurrency?: string;
  claim?: ClaimPolymorph;
  conversionRate?: number;
}

const sumDecimals = (amounts: Decimal[]): Decimal =>
  amounts.reduce((total, amount) => total.plus(amount), new Decimal(0));

// The VAT/GST the reserve movement is netted by: the payment's own `vat_amount`, in the policy
// currency the reserve is held in, or 0 where no netting applies.
//
// Rounded to cents because `payments_manager.convert_currency` rounds its quotient to two decimal
// places and `convertAmountForLimitCheck` does not, so an unrounded foreign-currency VAT leaves this
// preview and the gate it previews up to half a cent apart -- enough to cross a `toCents` boundary on
// the total. Clamped at zero because the backend clamps: `vat_amount` is captured at request time and
// never validated against the amount, and a negative figure would *raise* the netted movement.
export const getVatAmountToNetOut = ({
  shouldReduceReservesByNetPaid,
  isVatIncluded,
  vatAmount,
  requestCurrency,
  claim,
  conversionRate,
}: GetVatAmountToNetOutParams): number => {
  if (!shouldReduceReservesByNetPaid || !isVatIncluded || !vatAmount) {
    return 0;
  }
  return Math.max(
    0,
    toCents(
      convertAmountForLimitCheck({
        amount: Number(vatAmount),
        requestCurrency,
        claim,
        conversionRate,
      })
    )
  );
};

// Subtract the payment's VAT/GST from an amount the reserve will move by, clamped at zero -- the same
// shape as the backend's `_apply_net_to_reserve_amount`. Kept in `Decimal` and left unquantized for
// the reason that helper gives: rounding an intermediate term is what makes the two sides disagree.
const applyNetToReserveAmountDecimal = (
  amount: Decimal.Value | null | undefined,
  vatAmountToNetOut: number
): Decimal => {
  const amountDecimal = new Decimal(amount || 0);
  if (!vatAmountToNetOut) {
    return amountDecimal;
  }
  return Decimal.max(0, amountDecimal.minus(new Decimal(vatAmountToNetOut)));
};

export const applyNetToReserveAmount = (amount: Decimal.Value | null | undefined, vatAmountToNetOut: number): number =>
  applyNetToReserveAmountDecimal(amount, vatAmountToNetOut).toNumber();

// A payment carries one VAT/GST figure and no per-line breakdown, so it is apportioned pro rata by
// each line's share of the payment, with the residual of the pro rata on the largest line so the lines
// re-sum to the netted total -- mirroring `_get_netted_sub_reserve_amounts` on the backend.
//
// The whole apportionment runs in `Decimal` and nothing is quantized, exactly as the backend's does:
// rounding a line's pro-rata share to cents hands the difference to the largest line as residual, so
// `[333.33, 333.33, 333.34]` with 100 of VAT lands the third line on 300.00 here and on 300.006
// there -- against a 300.00 sub reserve that is a silent banner in front of a gate that answers 400.
export const getNettedSubReserveAmounts = (
  subReserveAmounts: SubReserveAmount[] | null | undefined,
  vatAmountToNetOut: number
): number[] => {
  const lines = subReserveAmounts || [];
  const grossAmounts = lines.map(({ amount }) => new Decimal(amount || 0));
  const totalGross = sumDecimals(grossAmounts);
  if (!vatAmountToNetOut || totalGross.lte(0)) {
    return lines.map(({ amount }) => amount);
  }

  const vatToNetOut = new Decimal(vatAmountToNetOut);
  // Each line gives up its pro-rata share of the VAT; a line whose share exceeds it clamps at zero.
  const nettedAmounts = grossAmounts.map((gross) =>
    Decimal.max(0, gross.minus(vatToNetOut.times(gross).div(totalGross)))
  );

  // Whatever the clamps swallowed is charged to the largest line, so the lines re-sum to the netted total.
  //
  // Left to right -- `line + total - soFar`, not `line + (total - soFar)`. `decimal.js` rounds every
  // operation to 20 significant digits, so re-associating those two moves where that rounding lands:
  // over 200k randomised line sets the two forms disagree on 25 of them, in the 20th digit. Far below
  // a cent, but this is the order `_get_netted_sub_reserve_amounts` folds the residual in on the
  // backend (`netted_amounts[largest_index] + netted_total - sum(netted_amounts)`), and the reason to
  // be in `Decimal` at all is to follow that line rather than approximate it.
  const largestIndex = grossAmounts.reduce((best, gross, index) => (gross.gt(grossAmounts[best]) ? index : best), 0);
  const nettedTotal = applyNetToReserveAmountDecimal(totalGross, vatAmountToNetOut);
  const nettedSoFar = sumDecimals(nettedAmounts);
  nettedAmounts[largestIndex] = Decimal.max(0, nettedAmounts[largestIndex].plus(nettedTotal).minus(nettedSoFar));

  return nettedAmounts.map((netted) => netted.toNumber());
};
